import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import createWebStorage from 'redux-persist/es/storage/createWebStorage';

import { Environment } from '@app/config/appConfig';
import { apiKeysApiSlice } from '@features/api-keys/api/api-keys.api';
import { assetManagementApiSlice } from '@features/asset-management/api/asset-management.api';
import { auditLogApiSlice } from '@features/audit-log/api/audit-log.api';
import { authenticationApiSlice } from '@features/authentication/api/authentication.api';
import { authBroadcastListenerMiddleware } from '@features/authentication/store/authentication.broadcast';
import { authListenerMiddleware } from '@features/authentication/store/authentication.listener';
import { proactiveRefreshListenerMiddleware } from '@features/authentication/store/authentication.proactive-refresh.listener';
import { authenticationReducer } from '@features/authentication/store/authentication.slice';
import { inquiryFindingsApiSlice } from '@features/inquiry-findings/api/inquiry-findings.api';
import { filesApiSlice } from '@features/investigations/api/files.api';
import { investigationsReducer } from '@features/investigations/store/investigations.slice';
import { tenantsApiSlice } from '@features/tenants/api/tenants.api';
import { tokenLedgerApiSlice } from '@features/token-ledger/api/token-ledger.api';
import { tokenManagementApiSlice } from '@features/token-management/api/token-management.api';
import { userManagementApiSlice } from '@features/user-management/api/user-management.api';
import { traceEventsApiSlice } from '@shared/api/findings/findings.api';
import { traceFindingsReducer } from '@shared/store/findings.slice';

const persistSessionStorage = createWebStorage('session');

const investigationPersistConfig = {
  key: 'investigations',
  storage: persistSessionStorage,
  whitelist: ['filesFilters'],
};

// The access token used to be persisted under this key. It now lives in memory only, so we
// proactively scrub any auth blob left in sessionStorage by a previous app version, ensuring
// no stale token survives a reload.

//TODO: remove in some months from now: 2026-06-25
if (typeof globalThis !== 'undefined' && globalThis.sessionStorage) {
  try {
    globalThis.sessionStorage.removeItem('persist:authentication');
  } catch (error) {
    // Catch SecurityError in privacy-restricted or embedded environments
    console.warn('Failed to clean up sessionStorage due to browser security restrictions:', error);
  }
}

const persistedInvestigationsReducer = persistReducer(investigationPersistConfig, investigationsReducer);

const rootReducer = {
  authentication: authenticationReducer,
  investigations: persistedInvestigationsReducer,
  traceFindings: traceFindingsReducer,
  [authenticationApiSlice.reducerPath]: authenticationApiSlice.reducer,
  [filesApiSlice.reducerPath]: filesApiSlice.reducer,
  [inquiryFindingsApiSlice.reducerPath]: inquiryFindingsApiSlice.reducer,
  [traceEventsApiSlice.reducerPath]: traceEventsApiSlice.reducer,
  [assetManagementApiSlice.reducerPath]: assetManagementApiSlice.reducer,
  [userManagementApiSlice.reducerPath]: userManagementApiSlice.reducer,
  [apiKeysApiSlice.reducerPath]: apiKeysApiSlice.reducer,
  [tenantsApiSlice.reducerPath]: tenantsApiSlice.reducer,
  [auditLogApiSlice.reducerPath]: auditLogApiSlice.reducer,
  [tokenLedgerApiSlice.reducerPath]: tokenLedgerApiSlice.reducer,
  [tokenManagementApiSlice.reducerPath]: tokenManagementApiSlice.reducer,
};

export const setupStore = () =>
  configureStore({
    reducer: rootReducer,
    devTools: import.meta.env.VITE_ENVIRONMENT !== Environment.PROD,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [PERSIST, REHYDRATE, PURGE, FLUSH, PAUSE, REGISTER], // to prevent serializable state errors with redux-persist
        },
      }).concat(
        authListenerMiddleware.middleware,
        authBroadcastListenerMiddleware.middleware,
        proactiveRefreshListenerMiddleware.middleware,
        authenticationApiSlice.middleware,
        filesApiSlice.middleware,
        inquiryFindingsApiSlice.middleware,
        traceEventsApiSlice.middleware,
        assetManagementApiSlice.middleware,
        userManagementApiSlice.middleware,
        apiKeysApiSlice.middleware,
        tenantsApiSlice.middleware,
        auditLogApiSlice.middleware,
        tokenLedgerApiSlice.middleware,
        tokenManagementApiSlice.middleware,
      ),
  });

export const store = setupStore();

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, UnknownAction>;
export type AppStore = ReturnType<typeof setupStore>;

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
export const useAppStore = useStore.withTypes<AppStore>();
